import { Component, Inject, OnDestroy, OnInit, DOCUMENT } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { EnvironmentService } from '@galaxy/core';
import { BrandingV2Service } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { StorefrontService } from '@vendasta/storefront';
import { Subscription, combineLatest } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import * as hotjar from '../lib/hotjar';
import { ImageService } from './core/image.service';
import { StyleConfigService } from './core/style-config.service';
import { ThemingService } from './theming.service';

declare const deployment: string;
declare const HOTJAR_TRACKING_CODE: string;

@Component({
  selector: 'app-root',
  template: `
    <div class="app-container">
      <strfrnt-storefront></strfrnt-storefront>
    </div>
  `,
  styles: [],
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  private readonly subscriptions: Subscription[] = [];

  constructor(
    private snowplowTracker: ProductAnalyticsService,
    private environmentService: EnvironmentService,
    private styleConfigService: StyleConfigService,
    private activatedRoute: ActivatedRoute,
    translate: TranslateService,
    private imageService: ImageService,
    private storefrontService: StorefrontService,
    private themingService: ThemingService,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private brandingService: BrandingV2Service,
  ) {
    translate.setDefaultLang('en');
    this.setFavicon();
  }

  ngOnInit(): void {
    this.themingService.initializeTheme(this.route.queryParams);
    this.subscriptions.push(
      this.activatedRoute.queryParamMap.subscribe((paramMap) => {
        this.styleConfigService.setQueryParams(paramMap);
      }),
    );
    if (HOTJAR_TRACKING_CODE) {
      hotjar.initialize(HOTJAR_TRACKING_CODE);
    }
    this.subscriptions.push(
      this.storefrontService.partnerId$.pipe(filter((partnerId) => !!partnerId)).subscribe((partnerId) => {
        const currentEnvironment = this.environmentService.getEnvironment();
        if (typeof deployment !== 'undefined') {
          this.snowplowTracker.initialize({
            environment: currentEnvironment,
            projectUUID: 'b61b4faa-8f0b-4ed4-90af-fb17818263ba',
            postHogID: 'phc_eq9tHnx8ASmHsj59RRQsO3jcR0DLBob7txIIpYBYruW',
            projectName: 'public-store',
            partner: {
              pid: partnerId,
            },
          });
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private setFavicon(): void {
    const defaultFaviconURL = this.imageService.getFileSrc('favicon.ico');
    combineLatest([this.storefrontService.partnerId$, this.storefrontService.marketId$])
      .pipe(
        filter(([partnerId, marketId]) => !!partnerId && !!marketId),
        switchMap(([partnerId, marketId]) => this.brandingService.getBranding(partnerId, marketId)),
        take(1),
      )
      .subscribe((branding) => {
        this._document.getElementById('app-favicon').setAttribute('href', branding?.faviconUrl || defaultFaviconURL);
      });
  }
}
